import { useQuery } from '@tanstack/react-query'
import { ListPlus, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { LabelTemplateSummaryType } from '@aeris/contracts'
import { formatMoney, fromString, UNIT_PRICE_SCALE } from '@aeris/money'
import {
  labelFor,
  labelSheetsFor,
  renderLabelSheetHtml,
  renderLayoutSheetHtml,
} from '@aeris/printing'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
} from '@aeris/ui'

import { fetchLabelTemplates } from '../lib/api/labels.ts'
import { useAuth } from '../lib/auth-context.ts'
import { printHtmlDocument } from '../lib/label-print.ts'
import { addToLabelQueue, droppedMessage } from '../lib/label-queue.ts'
import { labelSheetSummary } from '../lib/label-sheet-summary.ts'
import { readLabelTemplate } from '../lib/label-template.ts'
import { printedOnToday } from '../lib/printed-on.ts'
import { usePreference } from '../lib/use-preference.ts'

import { LabelSheetPreview } from './label-sheet-preview.tsx'
import { PrintSettingsHint } from './print-settings-hint.tsx'

/**
 * Imprimir a etiqueta de UM produto, ali mesmo — F127.
 *
 * O botão "Etiqueta" da lista só enchia a fila, que mora em outra tela, e quem
 * clicou achou que nada tinha acontecido. O dono pediu o contrário: escolher o
 * modelo e a quantidade, ver quantas folhas vão sair e imprimir sem sair do
 * catálogo. A fila continua existindo para o lote — "Pôr na fila" é o caminho
 * de quem vai mandar vários antes de imprimir.
 *
 * ## A impressora
 *
 * Quem escolhe é o diálogo do sistema, que abre ao clicar em Imprimir. Um
 * navegador não enxerga as impressoras do Windows, e o app desktop ainda não
 * tem comando de impressão (decisão do dono, 13/09: diálogo agora, lista de
 * impressoras no desktop numa etapa própria).
 */

export type LabelPrintProduct = {
  readonly variantId: string
  readonly productId: string
  readonly name: string
  readonly sku: string
  readonly gtin: string | null
  /** O preço base da tabela padrão, como vem da API. Nulo imprime sem preço. */
  readonly salePrice: string | null
  /** A última compra, já formatada (`01/09/2026`). Nula sai em branco. */
  readonly lastPurchaseAt: string | null
  readonly brand: string | null
  readonly unit: string | null
  /** F141: o fornecedor, e a localização nesta loja. */
  readonly supplierReference?: string | null
  readonly location?: string | null
}

const MAX_LABELS = 500

/** Inteiro dentro dos limites, ou nulo — "abc" e "0" não imprimem nada. */
const wholeBetween = (
  text: string,
  min: number,
  max: number
): number | null => {
  if (!/^\d+$/.test(text.trim())) return null
  const value = Number(text)
  return value >= min && value <= max ? value : null
}

export const LabelPrintDialog = ({
  product,
  onClose,
}: {
  product: LabelPrintProduct
  onClose: () => void
}): ReactNode => {
  const { session } = useAuth()
  const tenantId = session?.context?.tenantId ?? null

  const templates = useQuery({
    queryKey: ['label-templates'],
    queryFn: async ({ signal }) => await fetchLabelTemplates('active', signal),
    staleTime: 5 * 60_000,
  })

  /*
   * O último modelo escolhido, da PESSOA e não do computador (F127): quem
   * etiqueta no balcão de manhã e no depósito à tarde encontra o mesmo modelo
   * nos dois lugares.
   */
  const lastTemplate = usePreference('labels.lastTemplateId', {
    templateId: null,
  })
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [quantityText, setQuantityText] = useState('1')
  const [startText, setStartText] = useState('1')
  const [error, setError] = useState<string | null>(null)

  const available = templates.data ?? []
  const chosen: LabelTemplateSummaryType | undefined =
    available.find((entry) => entry.id === templateId) ??
    available.find((entry) => entry.id === lastTemplate.value.templateId) ??
    available.find((entry) => entry.isDefault) ??
    available[0]

  const quantity = wholeBetween(quantityText, 1, MAX_LABELS)
  const perSheet =
    chosen === undefined ? 1 : chosen.layout.columns * chosen.layout.rows
  const startAt =
    perSheet === 1 ? 1 : (wholeBetween(startText, 1, perSheet) ?? 1)

  const count =
    chosen === undefined
      ? labelSheetsFor({ columns: 1, rows: 1 }, quantity ?? 0)
      : labelSheetsFor(chosen.layout, quantity ?? 0, startAt)

  const simple = readLabelTemplate()
  const price =
    product.salePrice === null
      ? null
      : fromString(product.salePrice, UNIT_PRICE_SCALE)

  /*
   * O documento que vai para a impressora, e a prévia é um pedaço do MESMO
   * desenho: a primeira folha (ou o primeiro adesivo, no rolo). Desenhar 500
   * adesivos só para mostrar uma folha travaria a tela a cada tecla.
   */
  const htmlFor = (labels: number, skip: number): string =>
    chosen === undefined
      ? renderLabelSheetHtml(
          Array.from({ length: labels }, () =>
            labelFor(
              {
                name: product.name,
                sku: product.sku,
                gtin: product.gtin,
                price,
              },
              simple
            )
          ),
          simple
        )
      : renderLayoutSheetHtml(
          chosen.layout,
          Array.from({ length: labels }, () => ({
            name: product.name,
            sku: product.sku,
            gtin: product.gtin,
            salePrice: price === null ? null : formatMoney(price),
            salePriceAmount: product.salePrice,
            brand: product.brand,
            unit: product.unit,
            supplierReference: product.supplierReference ?? null,
            location: product.location ?? null,
            lastPurchaseAt: product.lastPurchaseAt,
          })),
          { skip, printedOn: printedOnToday() }
        )

  const previewLabels = Math.min(
    quantity ?? 1,
    count.isRoll ? 1 : count.perSheet - (startAt - 1)
  )

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title="Imprimir etiqueta"
        description={product.name}
        className="max-w-2xl"
      >
        <div className="flex flex-col gap-4">
          {templates.isError ? (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              Não foi possível carregar os modelos da loja — a etiqueta sai no
              formato simples do Aeris.
            </p>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Modelo" className="sm:col-span-3">
              {(field) => (
                <Select
                  {...field}
                  value={chosen?.id ?? ''}
                  disabled={available.length === 0}
                  onChange={(event) => {
                    setTemplateId(event.target.value)
                    lastTemplate.save({ templateId: event.target.value })
                  }}
                >
                  {available.length === 0 ? (
                    <option value="">
                      {templates.isPending
                        ? 'Carregando modelos…'
                        : 'Formato simples do Aeris'}
                    </option>
                  ) : (
                    available.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name} — {entry.layout.label.widthMm}×
                        {entry.layout.label.heightMm} mm
                        {entry.isDefault ? ' (padrão)' : ''}
                      </option>
                    ))
                  )}
                </Select>
              )}
            </Field>

            <Field
              label="Quantidade"
              error={
                quantity === null
                  ? `De 1 a ${String(MAX_LABELS)} etiquetas.`
                  : undefined
              }
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={quantityText}
                  onChange={(event) => {
                    setQuantityText(event.target.value)
                  }}
                />
              )}
            </Field>

            {count.isRoll ? null : (
              <Field
                label="Começar na posição"
                hint={`1 a ${String(count.perSheet)} — para folha já usada`}
              >
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    value={startText}
                    onChange={(event) => {
                      setStartText(event.target.value)
                    }}
                  />
                )}
              </Field>
            )}
          </div>

          <p
            aria-live="polite"
            className="text-(length:--text-md) font-(--weight-semibold)"
          >
            {quantity === null ? '—' : labelSheetSummary(quantity, count)}
          </p>

          <LabelSheetPreview
            title="Pré-visualização da etiqueta"
            htmlFor={htmlFor}
            layout={chosen?.layout}
            simple={simple}
            labelsOnSheet={previewLabels}
            skip={startAt - 1}
          />

          <PrintSettingsHint layout={chosen?.layout} />

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="secondary"
            disabled={quantity === null || tenantId === null}
            leadingIcon={<ListPlus aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              if (quantity === null || tenantId === null) return
              const { dropped } = addToLabelQueue(tenantId, [
                { ...product, quantity },
              ])
              // Fila cheia (F195): o diálogo fica aberto dizendo isso, em vez
              // de fechar como se tivesse entrado.
              const full = droppedMessage(dropped)
              if (full === null) onClose()
              else setError(full)
            }}
          >
            Pôr na fila
          </Button>
          <Button
            variant="primary"
            disabled={quantity === null}
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              if (quantity === null) return
              setError(null)
              printHtmlDocument(htmlFor(quantity, startAt - 1)).catch(
                (cause: unknown) => {
                  setError(
                    cause instanceof Error
                      ? cause.message
                      : 'Não foi possível abrir a impressão.'
                  )
                }
              )
            }}
          >
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
